import { useState } from "react";
import { PHOTOS, type PhotoName } from "@/lib/photos.gen";
import { cn } from "./cn";

export type { PhotoName };

/** The WebP of a photo at the smallest prepared width that covers `width`. */
export function photoSrc(name: PhotoName, width = 1280) {
  const widths: readonly number[] = PHOTOS[name].widths;
  const w = widths.find((x) => x >= width) ?? widths[widths.length - 1];
  return `/images/${PHOTOS[name].asset}-${w}.webp`;
}

/**
 * A photo from images-src/ (see `npm run photos`). A blurred sketch shows
 * while it loads, then the photo settles in like ink drying.
 *
 * - `sizes`: how wide it is drawn, so the browser picks the right file.
 * - `fill`: cover the nearest positioned parent instead of keeping the
 *   photo's own proportions.
 * - `priority`: load at once (the first thing on screen).
 * - `drift`: a very slow zoom, for large backgrounds.
 * - `position`: what to keep when cropping; defaults to the photo's `focus`.
 * - `decorative`: hidden from screen readers when the text says it all.
 */
export function Photo({ name, sizes = "100vw", fill, priority, drift, decorative, position, className, imgClassName }: { name: PhotoName; sizes?: string; fill?: boolean; priority?: boolean; drift?: boolean; decorative?: boolean; position?: string; className?: string; imgClassName?: string }) {
  const photo = PHOTOS[name];
  const [loaded, setLoaded] = useState(false);
  return (
    <div
      className={cn("overflow-hidden bg-cover", fill ? "absolute inset-0" : "relative", className)}
      style={{ backgroundImage: `url(${photo.placeholder})`, backgroundPosition: position ?? photo.focus, aspectRatio: fill ? undefined : `${photo.width} / ${photo.height}` }}
    >
      <img
        // A cached photo may finish before React attaches onLoad.
        ref={(img) => { if (img?.complete && img.naturalWidth) setLoaded(true); }}
        src={photoSrc(name)}
        srcSet={photo.widths.map((w) => `/images/${PHOTOS[name].asset}-${w}.webp ${w}w`).join(", ")}
        sizes={sizes}
        width={photo.width}
        height={photo.height}
        alt={decorative ? "" : photo.alt}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
        onLoad={() => setLoaded(true)}
        data-loaded={loaded || undefined}
        className={cn("photo size-full object-cover", drift && "photo-drift", imgClassName)}
        style={{ objectPosition: position ?? photo.focus }}
      />
    </div>
  );
}
